import { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { toast } from "sonner";
import { useBilling } from "../context/BillingContext";
import { Plus, Trash2, Crown, User, Mail, Loader2, RotateCw } from "lucide-react";

export default function Seats() {
  const { plan, isOrgAdmin, refresh } = useBilling();
  const [org, setOrg] = useState(null);
  const [features, setFeatures] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviteFeats, setInviteFeats] = useState({});
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(null);

  const load = async () => {
    const [{ data }, plans] = await Promise.all([
      api.get("/org/me"),
      api.get("/billing/plans"),
    ]);
    setOrg(data);
    setFeatures(plans.data.seat_features || []);
    if (!Object.keys(inviteFeats).length) {
      const defaults = {};
      (plans.data.seat_features || []).forEach(f => defaults[f.key] = true);
      setInviteFeats(defaults);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (plan !== "enterprise") {
    return <AppShell eyebrow="Team Seats" title="Enterprise required">
      <div className="border border-zinc-200 p-8 bg-zinc-50">
        <div className="text-sm text-zinc-700 mb-4">Team seats are part of the Enterprise plan. Upgrade to invite teammates and customize their access.</div>
        <a href="/billing" className="bg-[#0033FF] text-white px-5 py-3 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 inline-flex items-center gap-2" data-testid="goto-billing">See plans</a>
      </div>
    </AppShell>;
  }

  if (!isOrgAdmin) {
    return <AppShell eyebrow="Team Seats" title="Admin only">
      <div className="text-sm text-zinc-600">Only the organization admin can manage seats.</div>
    </AppShell>;
  }

  const invite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/org/seats/invite", { email: inviteEmail.trim(), role: inviteRole, features: inviteFeats });
      setInviteEmail("");
      if (data.attached_existing) toast.success("Existing user added to your team");
      else if (data.email_sent) toast.success("Invite email sent");
      else toast.error("Invite saved, but the email couldn't be delivered. Try Resend.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Invite failed");
    } finally { setBusy(false); }
  };

  const updateSeat = async (seatId, payload) => {
    try {
      await api.put(`/org/seats/${seatId}`, payload);
      toast.success("Seat updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const removeSeat = async (seatId) => {
    if (!confirm("Remove this seat?")) return;
    await api.delete(`/org/seats/${seatId}`);
    load();
  };

  const cancelInvite = async (inviteId) => {
    await api.delete(`/org/invites/${inviteId}`);
    load();
  };

  const resendInvite = async (inviteId) => {
    setResending(inviteId);
    try {
      const { data } = await api.post(`/org/invites/${inviteId}/resend`);
      data.email_sent ? toast.success("Invite re-sent") : toast.error("Email couldn't be delivered");
      load();
    } catch { toast.error("Resend failed"); }
    finally { setResending(null); }
  };

  const seats = org?.seats || [];
  const invites = org?.invites || [];
  const usedCount = seats.length + invites.length;
  const seatLimit = org?.org?.seat_limit || 10;

  return (
    <AppShell eyebrow="Enterprise" title={org?.org?.name || "Team Seats"}>
      <div className="text-sm text-zinc-600 mb-6">
        Manage who's on the team and what each seat can use. <span className="font-bold">{usedCount}</span> / {seatLimit} seats used.
      </div>
      {org?.seat_billing && (
        <div className="border-l-2 border-[#0033FF] pl-4 py-1 mb-8 text-sm" data-testid="seat-billing-summary">
          <div className="font-bold">
            {org.seat_billing.extra_seats > 0
              ? `${org.seat_billing.extra_seats} extra seat${org.seat_billing.extra_seats > 1 ? "s" : ""} · +$${org.seat_billing.extra_monthly.toFixed(2)}/mo`
              : `${org.seat_billing.base_seats} seats included in your plan`}
          </div>
          <div className="text-xs text-zinc-500">
            Seats beyond {org.seat_billing.base_seats} are ${org.seat_billing.price_per_extra_seat.toFixed(2)}/month each, added to your next monthly bill. Removing a teammate lowers the next bill.
          </div>
          {org.seat_billing.sync_error && <div className="text-xs text-[#FF3B30] font-bold mt-1" data-testid="seat-billing-error">We couldn't update your Stripe bill for the latest seat change — it will retry on the next change.</div>}
        </div>
      )}

      {/* Invite form */}
      <div className="border border-zinc-200 p-5 mb-8" data-testid="invite-form">
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#0033FF] mb-3">Invite a seat</div>
        <form onSubmit={invite} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input data-testid="invite-email" type="email" placeholder="teammate@company.com" value={inviteEmail} onChange={(e)=>setInviteEmail(e.target.value)} required
              className="sm:col-span-2 px-3 py-2.5 border border-zinc-300 focus:outline-none focus:border-[#0033FF] text-sm" />
            <select data-testid="invite-role" value={inviteRole} onChange={(e)=>setInviteRole(e.target.value)}
              className="px-3 py-2.5 border border-zinc-300 text-sm bg-white focus:outline-none focus:border-[#0033FF]">
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-600 mb-2">Features for this seat</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {features.map(f => (
                <label key={f.key} className="flex items-center gap-2 text-sm border border-zinc-300 px-3 py-2 cursor-pointer hover:bg-zinc-50" data-testid={`invite-feat-${f.key}`}>
                  <input type="checkbox" checked={!!inviteFeats[f.key]} onChange={(e)=>setInviteFeats(x=>({...x, [f.key]: e.target.checked}))} className="accent-[#0033FF]" />
                  {f.label}
                </label>
              ))}
            </div>
            <div className="text-xs text-zinc-500 mt-2">Note: Company Profile editing is always admin-only. Members can suggest ideas.</div>
          </div>
          <button type="submit" disabled={busy} data-testid="invite-submit"
            className="bg-[#0033FF] text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 disabled:opacity-50 inline-flex items-center gap-2">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Plus className="w-3.5 h-3.5" />} Invite
          </button>
        </form>
      </div>

      {org?.org && <WelcomeNoteEditor initial={org.org.welcome_note || ""} />}

      {/* Current seats */}
      <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-500 mb-2">Seats</div>
      <div className="border border-zinc-200 mb-8">
        {seats.length === 0 && <div className="p-6 text-sm text-zinc-500" data-testid="seats-empty">Just you for now.</div>}
        {seats.map(s => (
          <SeatRow key={s.id} seat={s} features={features} onUpdate={(p)=>updateSeat(s.id, p)} onRemove={()=>removeSeat(s.id)} />
        ))}
      </div>

      {/* Pending invites */}
      {invites.length > 0 && (
        <>
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-500 mb-2">Pending invites</div>
          <div className="border border-zinc-200">
            {invites.map(inv => (
              <div key={inv.id} className="p-4 border-b border-zinc-200 last:border-0 flex items-center justify-between" data-testid={`invite-row-${inv.id}`}>
                <div>
                  <div className="text-sm font-bold flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#0033FF]" />{inv.email}</div>
                  <div className="text-xs text-zinc-500 uppercase tracking-wider" data-testid={`invite-status-${inv.id}`}>
                    {inv.role} · sent {new Date(inv.last_sent_at || inv.created_at).toLocaleDateString()}
                    {inv.email_status === "failed" && <span className="text-[#FF3B30] font-bold"> · email failed</span>}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <button onClick={()=>resendInvite(inv.id)} disabled={resending === inv.id} data-testid={`resend-invite-${inv.id}`} className="text-xs font-bold uppercase tracking-wider text-[#0033FF] hover:text-zinc-900 disabled:opacity-50 inline-flex items-center gap-1">
                    {resending === inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />} Resend
                  </button>
                  <button onClick={()=>cancelInvite(inv.id)} data-testid={`cancel-invite-${inv.id}`} className="text-xs font-bold uppercase tracking-wider text-zinc-500 hover:text-[#FF3B30]">Cancel</button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </AppShell>
  );
}

function WelcomeNoteEditor({ initial }) {
  const [note, setNote] = useState(initial);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.put("/org/welcome-note", { welcome_note: note }); toast.success("Welcome note saved"); }
    catch { toast.error("Couldn't save note"); }
    finally { setSaving(false); }
  };
  return (
    <div className="border border-zinc-200 p-5 mb-8" data-testid="welcome-note-card">
      <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#0033FF] mb-1">Welcome email note</div>
      <div className="text-xs text-zinc-500 mb-3">A personal message from you, shown at the top of every new teammate's welcome email. Leave blank to skip.</div>
      <textarea data-testid="welcome-note-input" value={note} onChange={(e)=>setNote(e.target.value)} maxLength={1500} rows={4}
        placeholder="So glad you're here. Here's what I hope you'll feel in your first week…"
        className="w-full px-3 py-2.5 border border-zinc-300 focus:outline-none focus:border-[#0033FF] text-sm" />
      <div className="flex items-center justify-between mt-2">
        <div className="text-xs text-zinc-400">{note.length}/1500</div>
        <button onClick={save} disabled={saving} data-testid="welcome-note-save"
          className="bg-zinc-900 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#0033FF] disabled:opacity-50 inline-flex items-center gap-2">
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save note
        </button>
      </div>
    </div>
  );
}

function SeatRow({ seat, features, onUpdate, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  const feats = seat.seat_features || {};
  return (
    <div className="border-b border-zinc-200 last:border-0" data-testid={`seat-row-${seat.id}`}>
      <div className="p-4 flex items-center justify-between gap-3">
        <button onClick={()=>setExpanded(x=>!x)} className="text-left flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {seat.org_role === "admin" ? <Crown className="w-4 h-4 text-[#FFB800]" /> : <User className="w-4 h-4 text-zinc-500" />}
            <div className="text-sm font-bold">{seat.name || seat.email}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-bold">{seat.org_role}</div>
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">{seat.email}</div>
        </button>
        <div className="flex items-center gap-2">
          <select value={seat.org_role} onChange={(e)=>onUpdate({ role: e.target.value })} data-testid={`seat-role-${seat.id}`}
            className="px-2 py-1.5 border border-zinc-300 text-xs bg-white">
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
          <button onClick={onRemove} data-testid={`seat-remove-${seat.id}`} className="text-zinc-400 hover:text-[#FF3B30] p-2"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 grid grid-cols-2 md:grid-cols-4 gap-2">
          {features.map(f => (
            <label key={f.key} className="flex items-center gap-2 text-xs border border-zinc-200 px-3 py-2 cursor-pointer hover:bg-zinc-50" data-testid={`seat-feat-${seat.id}-${f.key}`}>
              <input type="checkbox" checked={feats[f.key] !== false} onChange={(e)=>onUpdate({ features: { ...feats, [f.key]: e.target.checked } })} className="accent-[#0033FF]" />
              {f.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
